import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import { Category } from "../models/Products/Category";

import { GetProductsParams } from "../models/Products/GetProductsParams";
import { Vendor } from "../models/Vendors/Vendor";
import {
  Product,
  ProductSEODetails,
  ProductSourcingDetails,
} from "../models/Products/Product";
import { getAPI, postAPI, putAPI } from "./Networking";
import { ProductOption } from "../models/Products/ProductOption";
import { ActivityLog } from "../models/Activity/ActivityLog";

export default class ProductAPI {
  getProductOptions = async (): Promise<ProductOption[]> => {
    try {
      const response = await getAPI(`/v5/products/product/options`);
      return response.result as ProductOption[];
    } catch (error) {
      throw error;
    }
  };

  getCategories = async (): Promise<Category[]> => {
    try {
      const response = await getAPI(`/v5/products/categories`);
      return response.result as Category[];
    } catch (error) {
      throw error;
    }
  };

  getProducts = async (
    params: GetProductsParams
  ): Promise<PaginatedAPIResponse<Product>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.sortBy) queryParams.append("sort_by", params.sortBy);
      if (params.sortType)
        queryParams.append("sort_type", params.sortType.toString());
      if (params.name) queryParams.append("name", params.name);
      if (params.category_id && params.category_id.length !== 0)
        queryParams.append("category_id", params.category_id.join(","));
      if (params.min_price !== undefined) {
        queryParams.append("min_price", params.min_price.toString());
      }
      if (params.max_price !== undefined) {
        queryParams.append("max_price", params.max_price.toString());
      }
      if (params.fast_procurement === true) {
        queryParams.append("fast_procurement", "true");
      }
      queryParams.append("active", params.active === false ? "0" : "1");
      // if (params.active)
      //
      // else queryParams.append("active", "true");
      const response = await getAPI(`/v5/products?${queryParams.toString()}`);
      return response.result as PaginatedAPIResponse<Product>;
    } catch (error) {
      throw error;
    }
  };

  getVendors = async (): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();
      const response = await getAPI(`/v5/vendors?${queryParams.toString()}`);
      return response.result.list as Vendor[];
    } catch (error) {
      throw error;
    }
  };

  getVendorsByName = async (name: string): Promise<any> => {
    try {
      // const queryParams = new URLSearchParams();

      const response = await getAPI(`/v5/vendors?search_text=${name}`);
      return response.result.list as Vendor[];
    } catch (error) {
      throw error;
    }
  };
  getProductDetailsById = async (productId: number) => {
    try {
      const response = await getAPI(`/v5/products/product-id/${productId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  createProduct = async (productDetails: Product): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/products/product`, productDetails);
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateProductDetails = async (product: Product): Promise<APIResponse> => {
    try {
      const {
        vendors,
        hsn_code,
        gst_percentage,
        fast_procurement,
        zoho_id,
        printing_types,
        seo_title,
        seo_description,
        ...productSectionFields
      } = product;

      const response = await putAPI(
        `/v5/products/product`,
        productSectionFields
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateAllProductDetails = async (product: Product): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/products/product`, product);
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateProductImages = async (payload: {
    product: Product;
    size_chart: number;
  }): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/products/product/images`, payload);
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateVariantImage = async (product: Product): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/products/product/variant-images`,
        product
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  duplicateProduct = async (id: number): Promise<APIResponse> => {
    try {
      const body = {
        product_id: id,
      };
      const response = await postAPI(`/v5/products/poduct/duplicate`, body);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPrintingTypes = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/products/printing-types`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  lastSoldProductsDetails = async (productIds: number | number[]) => {
    try {
      const ids = Array.isArray(productIds) ? productIds : [productIds];

      const response = await postAPI(`/v5/products/last-sold-details`, {
        product_ids: ids,
      });

      return response;
    } catch (error) {
      throw error;
    }
  };

  updateProductSEO = async (seoDetails: ProductSEODetails) => {
    try {
      const response = await putAPI(
        `/v5/products/product/seo-details`,
        seoDetails
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateProductSource = async (
    sourcingData: ProductSourcingDetails,
    pr_id?: number
  ) => {
    try {
      let url = `/v5/products/product/sourcing-details`;
      if (pr_id) {
        url += `?pr_id=${pr_id}`;
      }

      const response = await putAPI(url, sourcingData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  setDefaultImage = async (
    productId: number,
    imageId: number
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/products/product/set-default-image`, {
        product_id: productId,
        image_id: imageId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestInsights = async (
    productId: number
  ): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/products/insights/id/${productId}`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getProductActivityLogs = async (productId: number): Promise<ActivityLog[]> => {
    try {
      const response = await getAPI(`/v5/products/${productId}/activity-logs`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
